import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';
import ActivityItem from './ActivityItem';
import LoadingSpinner from '../../loading-spinner';
import styles from './ActivityDropdown.module.scss';

interface ActivityDropdownProps {
  type: 'approvals' | 'invites' | 'review' | 'signing' | 'out_for_signing';
  items: any[];
  onClose: () => void;
  onOpenPendingInvitesModal?: () => void;
  loading?: boolean;
  teamId?: string; // Add teamId prop for correct navigation
  totalCount?: number; // Total count from the count query
  buttonRef?: React.RefObject<HTMLElement>; // Reference to the button that opened this dropdown
}

const INITIAL_ITEMS_COUNT = 10;
const ITEMS_PER_LOAD = 20;
const VIEW_ALL_INITIAL_COUNT = 30;

const ActivityDropdown: React.FC<ActivityDropdownProps> = ({
  type,
  items,
  onClose,
  onOpenPendingInvitesModal,
  loading,
  teamId,
  totalCount,
  buttonRef,
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const [displayCount, setDisplayCount] = useState(INITIAL_ITEMS_COUNT);
  const [isExpanded, setIsExpanded] = useState(false);

  // Calculate dropdown position relative to button
  const calculateDropdownPosition = useCallback(() => {
    if (!buttonRef?.current || !dropdownRef.current) return;
    
    const buttonRect = buttonRef.current.getBoundingClientRect();
    const isMobile = window.innerWidth <= 768;
    
    if (isMobile) {
      // Mobile: Center dropdown and calculate pointer position
      const dropdownRect = dropdownRef.current.getBoundingClientRect();
      const buttonCenter = buttonRect.left + (buttonRect.width / 2);
      const dropdownLeft = dropdownRect.left;
      const pointerOffset = buttonCenter - dropdownLeft;
      
      dropdownRef.current.style.setProperty('--pointer-position', `${pointerOffset}px`);
      dropdownRef.current.style.setProperty('--dropdown-top', `${buttonRect.bottom + 8}px`);
    } else {
      // Desktop: Position dropdown below and aligned with button
      const dropdownLeft = buttonRect.left;
      const dropdownTop = buttonRect.bottom + 8;
      
      // Check if dropdown would go off the right edge of viewport
      const dropdownWidth = 320; // Width defined in CSS
      const viewportWidth = window.innerWidth;
      let finalLeft = dropdownLeft;
      
      if (dropdownLeft + dropdownWidth > viewportWidth - 20) {
        // Align to right edge of button instead
        finalLeft = buttonRect.right - dropdownWidth;
      }
      
      // Ensure dropdown doesn't go off the left edge
      finalLeft = Math.max(10, finalLeft);
      
      dropdownRef.current.style.setProperty('--dropdown-left', `${finalLeft}px`);
      dropdownRef.current.style.setProperty('--dropdown-top', `${dropdownTop}px`);
    }
  }, [buttonRef]);

  // Set up dropdown position calculation
  useEffect(() => {
    // Initial calculation
    setTimeout(calculateDropdownPosition, 0); // Use timeout to ensure DOM is ready
    
    // Recalculate on scroll/resize
    const handleRecalculate = () => calculateDropdownPosition();
    window.addEventListener('resize', handleRecalculate);
    window.addEventListener('scroll', handleRecalculate, true); // Capture phase for scroll
    
    return () => {
      window.removeEventListener('resize', handleRecalculate);
      window.removeEventListener('scroll', handleRecalculate, true);
    };
  }, [calculateDropdownPosition]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        // Check if click is not on the button itself
        const button = (event.target as Element)?.closest('[class*="activityButton"]');
        if (!button) {
          onClose();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (!loadMoreRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && displayCount < items.length) {
          // Load more items when scrolling near the bottom
          setDisplayCount((prev) => Math.min(prev + ITEMS_PER_LOAD, items.length));
        }
      },
      {
        root: contentRef.current,
        rootMargin: '50px',
        threshold: 0.1,
      }
    );

    const currentElement = loadMoreRef.current;
    if (currentElement) {
      observer.observe(currentElement);
    }

    return () => {
      if (currentElement) {
        observer.unobserve(currentElement);
      }
    };
  }, [displayCount, items.length]);

  const handleItemClick = (item: any) => {
    if (type === 'invites') {
      // For deal invites, open the modal instead of navigating
      if (onOpenPendingInvitesModal) {
        onOpenPendingInvitesModal();
      }
    } else if (type === 'approvals' || type === 'review' || type === 'signing' || type === 'out_for_signing') {
      // Navigate to the document or deal based on status
      const dealId = item.deal?.id || item.dealId;
      const documentId = item.id;

      if (dealId && teamId) {
        // External review documents must not open in the editor — the reviewer has no edit rights on them.
        if (type === 'review' && item.status === 'reviewing_external') {
          // External review - navigate to deal page only
          navigate(`/teams/${teamId}/deals/${dealId}`);
        } else if (documentId) {
          // Internal review, approvals, or signing - navigate to document
          if (type === 'signing') {
            navigate(`/teams/${teamId}/deals/${dealId}/documents/${documentId}/sign`);
          } else if (type === 'out_for_signing') {
            // For documents out for signing, navigate to document detail page
            navigate(`/teams/${teamId}/deals/${dealId}/documents/${documentId}`);
          } else {
            navigate(`/teams/${teamId}/deals/${dealId}/documents/${documentId}`);
          }
        }
      }
    }
    onClose();
  };

  const handleViewAll = useCallback(() => {
    setIsExpanded(true);
    setDisplayCount(Math.min(VIEW_ALL_INITIAL_COUNT, items.length));
    // Scroll to top of dropdown content for better UX
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [items.length]);

  const getTitle = () => {
    switch (type) {
      case 'approvals':
        return 'Pending Approvals';
      case 'invites':
        return 'Deal Invitations';
      case 'review':
        return 'Documents in Review';
      case 'signing':
        return 'Signing Preparation';
      case 'out_for_signing':
        return 'Out for Signing';
      default:
        return '';
    }
  };

  const displayItems = items.slice(0, displayCount);
  const hasMore = items.length > displayCount;
  const canLoadMore = displayCount < items.length;

  // Use portal to render dropdown at body level to escape stacking contexts
  return createPortal(
    <div ref={dropdownRef} className={classNames(styles.dropdown, styles[`dropdown--${type}`])}>
      <div className={styles.dropdownHeader}>
        <h3 className={styles.dropdownTitle}>
          <FormattedMessage id={`activity.${type}.title`} defaultMessage={getTitle()} />
        </h3>
        <span className={styles.dropdownCount}>{totalCount || items.length}</span>
      </div>

      <div
        ref={contentRef}
        className={classNames(styles.dropdownContent, isExpanded && styles.dropdownContentExpanded)}
      >
        {loading ? (
          <div className={styles.loadingWrapper}>
            <LoadingSpinner />
          </div>
        ) : items.length === 0 ? (
          <div className={styles.emptyState}>
            <FormattedMessage id={`activity.${type}.empty`} defaultMessage="No pending items" />
          </div>
        ) : (
          <>
            {displayItems.map((item, index) => (
              <ActivityItem key={item.id || index} item={item} type={type} onClick={() => handleItemClick(item)} />
            ))}

            {/* Sentinel element for infinite scroll */}
            {canLoadMore && (
              <div ref={loadMoreRef} className={styles.loadingMore}>
                <LoadingSpinner size="small" />
                <span>Loading more...</span>
              </div>
            )}

            {/* View All button - only show if not expanded and has more items */}
            {hasMore && !isExpanded && (
              <div
                className={styles.viewAll}
                onClick={handleViewAll}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleViewAll();
                  }
                }}
              >
                <FormattedMessage
                  id="activity.viewAll"
                  defaultMessage={`View all ${totalCount || items.length} items`}
                />
              </div>
            )}

            {/* Show count when expanded */}
            {isExpanded && !canLoadMore && items.length > VIEW_ALL_INITIAL_COUNT && (
              <div className={styles.itemCount}>Showing all {items.length} items</div>
            )}
          </>
        )}
      </div>
    </div>,
    document.body
  );
};

export default ActivityDropdown;
